import { FC, useContext, useEffect, useState } from 'react';

import { Button, H2, Input, Modal, Toggle } from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';

import { ActionSchedule } from 'components';
import { LocalizationContext } from 'localization';
import notificationService from 'services/notificationService';
import { SettingsContext } from 'settings';
import { secondsToWeekDays, weekDaysToSeconds } from 'utils';

import { DEFAULT_GROUP_VALUES } from '../contants';
import { IGroupFormValues, IGroupManageProps } from '../interfaces';
import s from './GroupManager.module.scss';

export const GroupManager: FC<IGroupManageProps> = ({
  group,
  createCategory,
  deleteCategory,
  updateCategory,
  onClose,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { shellGoodsDisplayEnabled },
    },
  } = useContext(SettingsContext);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isDirty, isSubmitting, isValid },
  } = useForm<IGroupFormValues>({
    mode: 'onChange',
    defaultValues: DEFAULT_GROUP_VALUES,
  });

  useEffect(() => {
    if (!group) {
      reset(DEFAULT_GROUP_VALUES);
      return;
    }

    const hasSellPeriods = group?.sell_periods && group.sell_periods.length > 0;

    reset({
      ...DEFAULT_GROUP_VALUES,
      title: group.title,
      show_in_shell: group.show_in_shell,
      days: hasSellPeriods
        ? group.sell_periods.map(
            (schedule) => secondsToWeekDays(schedule.start).day
          )
        : [],
      hours:
        hasSellPeriods && group?.sell_periods[0]
          ? {
              start: secondsToWeekDays(group.sell_periods[0]?.start)?.hour,
              end: secondsToWeekDays(group.sell_periods[0]?.end)?.hour,
            }
          : { start: '', end: '' },
    });

    trigger();
  }, [group, reset, trigger]);

  const ALL_WEEK_DAYS = [1, 2, 3, 4, 5, 6, 7];

  return (
    <Modal onClose={onClose} className={s.modal}>
      <div className={s.wrapper}>
        <form
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
          className={s.form}
          onSubmit={handleSubmit((data) => {
            const validatedDays = data?.days.length
              ? data?.days
              : ALL_WEEK_DAYS;
            const validatedHours = data?.hours?.start
              ? data?.hours
              : { start: '00:00', end: '00:00' };

            const schedules = weekDaysToSeconds(validatedHours, validatedDays);

            const payload = {
              title: data.title,
              show_in_shell: data.show_in_shell,
              sell_periods: schedules,
            };

            if (group) {
              return updateCategory({
                id: group.id,
                ...payload,
              })
                .then(onClose)
                .catch(() => {
                  notificationService.info({
                    t,
                    type: 'error',
                    title: t('group.edit.error.title'),
                    message: t('group.edit.error'),
                  });
                });
            }

            return createCategory(payload)
              .then(onClose)
              .catch(() => {
                notificationService.info({
                  t,
                  type: 'error',
                  title: t('group.create.error.title'),
                });
              });
          })}
        >
          <H2 className={s.title}>
            {!group
              ? t('log.discount.good_group.created')
              : t('goods.group.modal.title')}
          </H2>
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                error={error?.message}
                title={t('goods.group.modal.input')}
                className={s.input}
                maxLength={50}
              />
            )}
            name="title"
            control={control}
            rules={{
              required: t('errors.required'),
              minLength: {
                value: 2,
                message: t('errors.minLength', 2),
              },
              maxLength: {
                value: 50,
                message: t('errors.maxLength', 50),
              },
            }}
          />

          <ActionSchedule control={control} trigger={trigger} />

          {shellGoodsDisplayEnabled && (
            <div className={s.toggle_block}>
              <Controller
                render={({ field }) => (
                  <Toggle
                    {...field}
                    label={t('goods.show_in_shell.label')}
                    className={s.toggle}
                    description={t('goods.category.shell.text')}
                  />
                )}
                name={'show_in_shell'}
                control={control}
              />
            </div>
          )}
          <div className={s.submit_container}>
            {group && (
              <Button
                isLoading={isDeleting}
                onClick={() => {
                  setIsDeleting(true);
                  return deleteCategory(group?.id)
                    .then(onClose)
                    .finally(() => {
                      setIsDeleting(false);
                    });
                }}
                variant="secondary"
                color="red"
                className={s.button}
              >
                {t('delete')}
              </Button>
            )}
            <Button
              type="submit"
              isLoading={isSubmitting}
              disabled={!isDirty || !isValid}
              className={s.button}
            >
              {t(group ? 'save' : 'create')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
